<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { filter } from 'lodash-es';
import TwTitleBar from '../../../components/TwTitleBar';

export default {
  name: 'SkiSchoolSeasons',
  components: {
    TwTitleBar,
  },
  data() {
    return {
      tabSelected: 'active',
      tabs: [
        {
          name: this.$t('ski.skiSeasons.dataHeaders.pricesActive'),
          identifier: 'active',
          total: 0,
        },
        {
          name: this.$t('ski.skiSeasons.dataHeaders.pricesNotActive'),
          identifier: 'inActive',
          total: 0,
        },
        {
          name: this.$t('ski.skiSeasons.dataHeaders.showOnWeb'),
          identifier: 'webActive',
          total: 0,
        },
        {
          name: this.$t('ski.skiSeasons.dataHeaders.hiddenFromWeb'),
          identifier: 'webInActive',
          total: 0,
        },
      ],
      dataHeaders: [
        { text: this.$t('skiSchool.dataHeaders.name'), value: 'name' },
        { text: this.$t('skiSchool.dataHeaders.currency'), value: 'netOrGross' },
        { text: this.$t('skiSchool.dataHeaders.personCategories'), value: 'numPersonCategories' },
        { text: this.$t('skiSchool.dataHeaders.areas'), value: 'numAreas' },
        { text: this.$t('skiSchool.dataHeaders.prices'), value: 'numPrices' },
        { text: this.$t('skiSchool.dataHeaders.levels'), value: 'numLevels' },
      ],
      pagination: {
        rowsPerPage: -1,
        descending: false,
        sortBy: 'name',
      },
      paginationLevels: [
        10,
        20,
        50,
        {
          text: '$vuetify.dataIterator.rowsPerPageAll',
          value: -1,
        },
      ],
      breadcrumbs: [
        { text: this.$t('menu.ski.title'), to: '', disabled: true },
        { text: this.$t(`breadcrumbs.${this.$route.name}`), to: this.$route.path },
      ],
    };
  },
  computed: {
    ...mapGetters({
      seasons: 'skischool/getSeasonsWithRelations',
    }),
    ...mapState({
      seasonsError: state => state.skischool.seasons.itemsError,
      seasonsLoading: state => state.skischool.seasons.itemsLoading,
    }),
    filteredSeasons() {
      let seasons = this.seasons;
      if (!seasons) {
        return [];
      }
      seasons = filter(this.seasons,
        season => (
          (this.tabSelected === 'active' && season.active)
          || (this.tabSelected === 'inActive' && !season.active)
          || (this.tabSelected === 'webActive' && season.webActive)
          || (this.tabSelected === 'webInActive' && !season.webActive)
        ),
      );
      this.updateMenuTotals();
      return seasons;
    },
  },
  methods: {
    ...mapActions({
      loadSeasons: 'skischool/seasonsLoad',
      resetState: 'areas/resetState',
    }),
    setTabActive(tabIdentifier) {
      this.tabSelected = tabIdentifier;
    },
    updateMenuTotals() {
      const tabs = this.tabs;
      const seasons = this.seasons;
      for (let i = 0; i < tabs.length; i += 1) {
        switch (tabs[i].identifier) {
          case 'active': {
            const seasonsActive = filter(seasons, season => season.active);
            tabs[i].total = seasonsActive.length;
            break;
          }
          case 'inActive': {
            const seasonsInActive = filter(seasons, season => !season.active);
            tabs[i].total = seasonsInActive.length;
            break;
          }
          case 'webActive': {
            const seasonsWebActive = filter(seasons, season => season.webActive);
            tabs[i].total = seasonsWebActive.length;
            break;
          }
          case 'webInActive': {
            const seasonsWebInActive = filter(seasons, season => !season.webActive);
            tabs[i].total = seasonsWebInActive.length;
            break;
          }
          default:
            break;
        }
      }
    },
    formatDate(date) {
      return date ? this.$moment(date).format('DD-MM-YY') : '';
    },
  },
  mounted() {
    this.loadSeasons();
  },
};
</script>

<template>
  <v-container fluid>
    <tw-title-bar
      :title="$t('menu.ski.pages.skiSchool.title')"
      :breadcrumbs="breadcrumbs">
    </tw-title-bar>
    <v-layout class="pt-2" row>
      <v-flex>
        <v-btn
          :to="{ name: 'NewSkiSchoolSeason', params: { seasonId: undefined, new: true } }"
          color="success"
          dark
        >
          {{ $t('ski.skiSeasons.newSeason') }}
        </v-btn>
        <v-btn
          @click="loadSeasons"
          :loading="seasonsLoading"
          icon
        >
          <v-icon>cached</v-icon>
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout fill-height row>
      <v-flex xs12>
        <v-container fluid>
          <v-card class="ski-area__card">
            <v-tabs id="select-container" grow fixed-tabs>
              <v-tab
                @click="setTabActive(tab.identifier)"
                v-for="tab in tabs"
                :key="tab.name"
                :disabled="!tab.total"
                exact
              >
                {{ tab.name }} ({{ tab.total }})
              </v-tab>
            </v-tabs>
            <div>
              <v-card v-if="!seasonsError">
                <v-data-table
                  :headers="dataHeaders"
                  :items="filteredSeasons"
                  :rows-per-page-items="paginationLevels"
                  :pagination.sync="pagination"
                  :loading="seasonsLoading"
                  must-sort
                >
                  <template v-slot:items="props">
                    <router-link
                      :to="{ name: 'SkiSchoolSeason', params: { seasonId: props.item.id } }"
                      tag="tr"
                      class="ski-areas__tr"
                    >
                      <td class="font-weight-medium">
                        {{ props.item.name }}
                        <v-chip
                          v-if="!props.item.active"
                          color="red"
                          class="text"
                          size="2"
                          outline
                        >
                          <v-icon left>error</v-icon>
                          {{ $t('ski.skiSeasons.dataHeaders.pricesNotActive') }}
                        </v-chip>
                      </td>
                      <td
                        :class="props.item.netOrGross === 'gross'
                          ? 'green--text'
                          : 'warning--text'"
                        class="font-weight-medium"
                      >
                        {{ props.item.netOrGross }}
                      </td>
                      <td
                        :class="props.item.numPersonCategories ? 'green--text' : 'red--text'"
                        class="font-weight-medium"
                      >
                        <v-icon
                          :color="props.item.numPersonCategories ? 'green' : 'red'"
                          class="ml-2"
                        >
                          {{ props.item.numPersonCategories ? 'check_circle' : 'error' }}
                        </v-icon>
                      </td>
                      <td
                        :class="props.item.numAreas ? 'green--text' : 'red--text'"
                        class="font-weight-medium"
                      >
                        {{ props.item.numAreas }}
                        <v-icon
                          :color="props.item.numAreas ? 'green' : 'red'"
                          class="ml-2"
                        >
                          {{ props.item.numAreas ? 'check_circle' : 'error' }}
                        </v-icon>
                      </td>
                      <td
                        :class="props.item.numPrices ? 'green--text' : 'red--text'"
                        class="font-weight-medium"
                      >
                        {{ props.item.numPrices }}
                        <v-icon
                          :color="props.item.numPrices ? 'green' : 'red'"
                          class="ml-2"
                        >
                          {{ props.item.numPrices ? 'check_circle' : 'error' }}
                        </v-icon>
                      </td>
                      <td
                        :class="props.item.numLevels ? 'green--text' : 'red--text'"
                        class="font-weight-medium"
                      >
                        {{ props.item.numLevels }}
                        <v-icon
                          :color="props.item.numLevels ? 'green' : 'red'"
                          class="ml-2"
                        >
                          {{ props.item.numLevels ? 'check_circle' : 'error' }}
                        </v-icon>
                      </td>
                    </router-link>
                  </template>
                </v-data-table>
              </v-card>
              <div v-if="!seasonsLoading && seasonsError" class="text-xs-center mt-5">
                {{ $t('skiAreas.error') }}
              </div>
            </div>
          </v-card>
        </v-container>
      </v-flex>
    </v-layout>
  </v-container>
</template>



// WEBPACK FOOTER //
// src/pages/ski/school/SkiSchoolSeasons.vue